import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { useAtomValue } from 'jotai'
import { paymentProcessState } from '../store/loadingState'
import CheckedSuccessIcon from '../../assets/icons/svg/CheckedSuccessIcon.svg?react'
import ProcessLoaderIcon from '../../assets/icons/svg/ProcessLoaderIcon.svg?react'
import TimeErrorIcon from '../../assets/icons/svg/TimeErrorIcon.svg?react'
import ErrorBox from '../errorBox/ErrorBox'

const LoadingModal = ({
    error = [],
    open,
    onClose,
    isFailed,
    purchaseDetailDictionaryKey
}: {
    error: string[]
    open: boolean
    onClose: () => void
    isFailed: boolean
    purchaseDetailDictionaryKey: string
}) => {
    const { translate } = useTranslate()
    const { orderProcessing, paymentProcessing, logProcessing } = useAtomValue(paymentProcessState)

    // Spinning animation style
    const spinningStyle = {
        animation: 'spin 1s linear infinite'
    }

    const LoaderIcon = (status: number) => {
        if (status === 1) {
            return <ProcessLoaderIcon style={spinningStyle} />
        } else if (status === 2) {
            return <CheckedSuccessIcon />
        } else if (status === -1) {
            return <TimeErrorIcon />
        }
    }

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose()
                }
            }}
            fullWidth
            maxWidth={'sm'}
            disableEscapeKeyDown={false}
            sx={{
                '& .MuiDialog-paper': {
                    padding: '32px',
                    margin: '16px',
                    borderRadius: '12px',
                    gap: '32px',
                    backgroundColor: 'rgba(0, 55, 100, 1)',
                    boxShadow: 'none',
                    // Add keyframes for spinning animation
                    '@keyframes spin': {
                        '0%': {
                            transform: 'rotate(0deg)'
                        },
                        '100%': {
                            transform: 'rotate(360deg)'
                        }
                    }
                }
            }}>
            <DialogContent sx={{ padding: '0px', gap: '24px', display: 'flex', flexDirection: 'column' }}>
                <Stack gap={'24px'}>
                    <Typography variant="loadingModalTitle">{translate('processing_your_purchase')}</Typography>
                    <Typography
                        variant="loadingModalSubtitle"
                        fontSize={{
                            xs: '16px',
                            sm: '20px'
                        }}>
                        {translate(purchaseDetailDictionaryKey)}
                    </Typography>
                </Stack>
                <Stack gap={'24px'}>
                    <Stack direction={'row'} gap={'16px'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography
                            variant="loadingModalSubtitle"
                            fontSize={{
                                xs: '16px',
                                sm: '20px'
                            }}>
                            {orderProcessing === 1 && translate('preparing_order_processing')}
                            {orderProcessing === 2 && translate('processing_order')}
                            {orderProcessing === -1 && translate('processing_order_failed')}
                        </Typography>
                        {LoaderIcon(orderProcessing)}
                    </Stack>
                    <Stack direction={'row'} gap={'16px'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography
                            variant="loadingModalSubtitle"
                            fontSize={{
                                xs: '16px',
                                sm: '20px'
                            }}>
                            {paymentProcessing === 1 && translate('preparing_payment')}
                            {paymentProcessing === 2 && translate('processing_payment')}
                            {paymentProcessing === -1 && translate('processing_payment_failed')}
                        </Typography>
                        {LoaderIcon(paymentProcessing)}
                    </Stack>
                    <Stack direction={'row'} gap={'16px'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography
                            variant="loadingModalSubtitle"
                            fontSize={{
                                xs: '16px',
                                sm: '20px'
                            }}>
                            {logProcessing === 1 && translate('preparing_log')}
                            {logProcessing === 2 && translate('update_log_processing')}
                            {logProcessing === -1 && translate('processing_log_failed')}
                        </Typography>
                        {LoaderIcon(logProcessing)}
                    </Stack>
                </Stack>
                {error.length > 0 && <ErrorBox messages={error} />}
                {(error.length > 0 || isFailed) && (
                    <Stack justifyContent={'center'} alignItems={'center'}>
                        <Button variant="contained" disableElevation color="primary" sx={{ width: '170px', marginTop: '24px' }} onClick={onClose}>
                            {translate('CLOSE')}
                        </Button>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default LoadingModal
